/**
 * Sentry configuration for Blackbird Auction.
 * DSN, sample rate, and environment come from webpack DefinePlugin (env / .env files).
 */

/**
 * Get Sentry DSN from build-time env (webpack DefinePlugin or process.env).
 * Prefer not committing the real DSN; inject in CI (e.g. SENTRY_DSN).
 */
export function getSentryDSN() {
  return typeof process.env.REACT_APP_SENTRY_DSN !== "undefined"
    ? process.env.REACT_APP_SENTRY_DSN
    : "";
}

/**
 * Get traces sample rate by environment (e.g. 1.0 for dev/qa, 0.1 for production).
 */
export function getTracesSampleRate() {
  const env =
    typeof process.env.REACT_APP_ENVIRONMENT !== "undefined"
      ? process.env.REACT_APP_ENVIRONMENT
      : process.env.NODE_ENV || "development";
  switch (env) {
    case "production":
      return 0.1;
    case "uat":
      return 0.3;
    case "qa":
      return 0.5;
    default:
      return 1.0;
  }
}

/**
 * Get environment/deployment name for Sentry (used for both environment and tenant tagging).
 */
export function getEnvironment() {
  return (
    process.env.REACT_APP_ENVIRONMENT ||
    process.env.NODE_ENV ||
    "unknown"
  );
}

/**
 * Scrub sensitive data from event before sending to Sentry.
 * Wrapped in try/catch so Sentry failures never break the app.
 * @param {object} event - Sentry event
 * @returns {object | null} event (or null to drop)
 */
export function beforeSend(event) {
  try {
    if (!event) return null;
  if (event.request && event.request.data) {
    const data = event.request.data;
    if (typeof data === "object" && data !== null && !Array.isArray(data)) {
      const sensitiveFields = [
        "password",
        "token",
        "accessToken",
        "refreshToken",
        "authorization",
        "apiKey",
        "secret",
      ];
      sensitiveFields.forEach((field) => {
        if (field in data && typeof data[field] !== "undefined") {
          data[field] = "[Filtered]";
        }
      });
    }
  }
  if (event.extra && typeof event.extra === "object") {
    const sensitiveKeys = ["token", "password", "authorization"];
    sensitiveKeys.forEach((key) => {
      if (key in event.extra && typeof event.extra[key] !== "undefined") {
        event.extra[key] = "[Filtered]";
      }
    });
  }
  if (
    event.request &&
    event.request.headers &&
    typeof event.request.headers === "object"
  ) {
    const headers = event.request.headers;
    ["authorization", "cookie", "x-api-key"].forEach((header) => {
      const lower = header.toLowerCase();
      if (lower in headers && typeof headers[lower] !== "undefined") {
        headers[lower] = "[Filtered]";
      }
    });
  }
  return event;
  } catch (_) {
    return event != null ? event : null;
  }
}
